<script setup lang="ts">
import { type TherapeuticLoadRow } from '@/api/patient-monthly-metrics';
import { getCareerColor } from '@/utils/careers';

defineProps<{
  rows: TherapeuticLoadRow[];
}>();

const PERCENT = 100;

type VisitsKey = 'agreed' | 'budgeted' | 'planned';

type Chip = { text: string; color: string };

const VISITS_LABELS: Record<VisitsKey, string> = {
  agreed: 'Acordado',
  budgeted: 'Presupuestado',
  planned: 'Agendado',
};

// Each stage is compared with the previous one; the header chip compares the ends.
const VISITS_LINES: { key: VisitsKey; comparedTo?: VisitsKey }[] = [
  { key: 'agreed' },
  { key: 'budgeted', comparedTo: 'agreed' },
  { key: 'planned', comparedTo: 'budgeted' },
];

function deviationChip(row: TherapeuticLoadRow): Chip {
  if (row.agreed === 0) {
    return row.planned > 0 ?
      { text: 'Sin visitas acordadas', color: 'warning' } :
      { text: 'Alineado', color: 'default' };
  }
  const deviation = Math.round(((row.planned - row.agreed) / row.agreed) * PERCENT);
  if (row.planned > row.agreed) return { text: `Sobre-tope (+${deviation}%)`, color: 'error' };
  if (row.planned < row.agreed) return { text: `Bajo tope (${deviation}%)`, color: 'default' };

  return { text: 'Alineado', color: 'success' };
}

function differenceChip(row: TherapeuticLoadRow, key: VisitsKey, comparedTo: VisitsKey): Chip {
  const difference = row[key] - row[comparedTo];
  const reference = `vs. ${VISITS_LABELS[comparedTo].toLowerCase()}`;
  if (difference > 0) return { text: `+${difference} ${reference}`, color: 'error' };
  if (difference < 0) return { text: `${difference} ${reference}`, color: 'warning' };

  return { text: `±0 ${reference}`, color: 'success' };
}
</script>

<template>
  <v-card class="pa-4 h-100 d-flex flex-column">
    <div class="d-flex align-center justify-space-between flex-wrap ga-1 mb-1">
      <span class="text-subtitle-1 font-weight-bold">Carga Terapéutica</span>
      <v-chip
        size="small"
        variant="tonal"
        label
      >
        Acordado · Presupuestado · Agendado
      </v-chip>
    </div>
    <p class="text-caption text-medium-emphasis mb-3">
      Monitoreo clínico del plan prescrito: acordado en los requerimientos, presupuestado en
      los planes semanales y agendado en el calendario. Las desviaciones positivas indican
      sobreagendamiento (riesgo de costos).
    </p>
    <p
      v-if="rows.length === 0"
      class="text-body-2 text-medium-emphasis text-center py-4"
    >
      Sin plan de cuidados para este mes.
    </p>
    <v-card
      v-for="row in rows"
      :key="row.career"
      :elevation="1"
      class="pa-3 mb-3"
    >
      <div class="d-flex align-center justify-space-between flex-wrap ga-1 mb-2">
        <span
          class="font-weight-bold"
          :class="`text-${getCareerColor(row.career)}`"
        >
          {{ row.humanCareer }}
        </span>
        <v-chip
          size="small"
          :color="deviationChip(row).color"
          variant="tonal"
          label
          data-testid="deviation-chip"
        >
          {{ deviationChip(row).text }}
        </v-chip>
      </div>
      <div class="visits-container">
        <div class="visits-grid">
          <div
            v-for="line in VISITS_LINES"
            :key="line.key"
            class="visits-line"
            :data-testid="`${line.key}-visits`"
          >
            <span class="text-body-2 text-medium-emphasis">
              {{ VISITS_LABELS[line.key] }}
            </span>
            <span
              class="font-weight-bold text-right text-no-wrap"
              :class="{ 'text-primary': line.key === 'planned' }"
            >
              {{ row[line.key] }} turnos
            </span>
            <span class="difference-cell">
              <v-chip
                v-if="line.comparedTo"
                size="x-small"
                :color="differenceChip(row, line.key, line.comparedTo).color"
                variant="tonal"
                label
                data-testid="difference-chip"
              >
                {{ differenceChip(row, line.key, line.comparedTo).text }}
              </v-chip>
            </span>
          </div>
        </div>
      </div>
    </v-card>
    <p class="text-caption text-medium-emphasis mb-0 mt-auto">
      <v-icon
        icon="mdi-alert-outline"
        size="x-small"
        color="warning"
      />
      Controla el sobre-tope clínico mensual para evitar descalces.
    </p>
  </v-card>
</template>

<style scoped>
.visits-container {
  container-type: inline-size;
}

.visits-grid {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 8px 12px;
}

.visits-line {
  display: contents;
}

/* Three-card rows at md leave no room for the chips beside the values. */
@container (max-width: 340px) {
  .visits-grid {
    grid-template-columns: 1fr auto;
    row-gap: 4px;
  }

  .difference-cell {
    grid-column: 1 / -1;
    justify-self: end;
  }

  .difference-cell:empty {
    display: none;
  }
}
</style>
